import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { RiContactsBookLine, RiPencilLine, RiStickyNoteLine } from "@remixicon/react";
import {
  getStudentsPage,
  countStudents,
  getStudentChildren,
  getUserById,
  getSubjectNames,
  getLatestAppVersion,
  searchPeople,
  PHONE_ALIASES,
} from "../../services/dataService";
import { isMissingIndexError } from "../../services/firestoreErrors";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import {
  PAGE_SIZES,
  STUDENT_FILTERS,
  STUDENT_SORTS,
  studentQuerySpec,
  isSearchTerm,
  needsChildren,
  interestsOf,
  chipOverflow,
  appCell,
  learnersLabel,
  statusChips,
  sessionsLabel,
  lastLessonLabel,
  nextLessonLabel,
  listSubtitle,
  emptyState,
} from "../../utils/studentPresentation";
import { contactCell } from "../../utils/staffNotesPresentation";
import { countText, dateTimeLines, showingOfLabel } from "../../utils/kitPresentation";
import { headButtonSx, tokens } from "../../utils/figmaTokens";
import { ListHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import { StateAction } from "../../components/kit/States";
import { EyeButton, PersonCell, RowAction, SkeletonRows, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import SearchField from "../../components/kit/SearchField";
import ToolbarMenu from "../../components/kit/ToolbarMenu";
import ViewChips from "../../components/kit/ViewChips";
import AddNoteDialog from "../../components/StaffNotes/AddNoteDialog";
import EditStudentDialog from "./EditStudentDialog";

// Spec 044 slice 4a (D19–D23), drawn from Figma's Users screen (047 D28–D34). The list is the
// server's: one cursor page for the (filter, sort) pair on screen, a count from an aggregation
// query, the Learners names from one bounded read per visible row. Each filter chip carries its
// own count: one aggregation per filter when the page opens (047 D29). Nothing here reads a
// collection. A row opens the student's own page (slice 4b, D29).

const phoneOf = (u) => PHONE_ALIASES.map((f) => u?.[f]).find(Boolean) || "";
const COLUMNS = ["Student", "Learners", "Subjects", "App", "Balance", "Last lesson", "Next lesson", "Status", "Contact", "Joined", "Actions"];
const MISSING_INDEX = "This view needs a Firestore index that is missing or still building.";

// The Subjects cell: up to two chips and "+N", "—" when the student chose none.
const SubjectChips = ({ interests }) => {
  if (interests.length === 0) {
    return (
      <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
        —
      </Typography>
    );
  }
  const { shown, more } = chipOverflow(interests);
  return (
    <Box sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
      {shown.map((name) => (
        <Chip key={name} label={name} />
      ))}
      {more > 0 && (
        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
          +{more}
        </Typography>
      )}
    </Box>
  );
};

const Students = () => {
  const { user } = useAuth();
  const canEdit = hasPermission(user?.role || "", permissions.EDIT_STUDENTS);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // list state — filter, sort and page live here; the query is rebuilt from
  // them on every load so a stale closure can never page a different list
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("newest");
  // 057 D14: one subject at a time, its own view (the only indexed shape), so choosing one resets
  // the filter chip and the sort, and choosing a chip or another sort resets it.
  const [subject, setSubject] = useState("");
  const [subjectNames, setSubjectNames] = useState([]);
  const [subjectCounts, setSubjectCounts] = useState({});
  const [latest, setLatest] = useState(null); // highest client.appVersion; the app chips wait for it
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null); // matching the filter; null until its count lands
  const [counts, setCounts] = useState({}); // filter key -> its count, for the chips; null where it failed
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // the error block's line; "" when the page loaded
  // startAfter cursors: cursors.current[p] is the last document of page p.
  const cursors = useRef([]);
  const loadToken = useRef(0); // the newest load wins; older ones drop their result
  const [childNames, setChildNames] = useState({}); // uid -> [name]

  // search state — the Ctrl+K people index, unpaged, ignoring filter and sort (D21)
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRows, setSearchRows] = useState(null); // null: not searching
  const [searching, setSearching] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null); // the row whose Add note dialog is open
  const [snack, setSnack] = useState(null); // { message }
  const noteTimers = useRef([]);

  // D13 / 002 D5: names come from users/{uid}/children, one read per row that
  // has (or may have) children — never from the user document. A failed read
  // is kept as an explicit null so the cell shows "—" rather than claiming
  // "Self only"; undefined still means "not read yet".
  const loadChildren = useCallback(async (list) => {
    const pairs = await Promise.all(
      list
        .filter(needsChildren)
        .map(async (r) => [r.id, await getStudentChildren(r.id).catch(() => null)])
    );
    setChildNames((prev) => ({
      ...prev,
      ...Object.fromEntries(pairs.map(([id, kids]) => [id, kids ? kids.map((k) => k.name) : null])),
    }));
  }, []);

  // Every chip's count, once per visit (047 D29): the page's own count query, one per filter,
  // each failing alone — a chip whose count failed shows no number. The two app chips count once
  // the latest version is known (057 D18).
  const countFilters = useCallback(async (list, latestVersion) => {
    const now = new Date();
    const values = await Promise.all(
      list.map((f) =>
        countStudents(studentQuerySpec({ filter: f.key, latest: latestVersion, now })).catch((e) => {
          console.error("Error counting students:", e);
          return null;
        })
      )
    );
    return Object.fromEntries(list.map((f, i) => [f.key, values[i]]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    countFilters(
      STUDENT_FILTERS.filter((f) => !f.needsLatest),
      null
    ).then((c) => !cancelled && setCounts((prev) => ({ ...prev, ...c })));
    getLatestAppVersion()
      .then((v) => !cancelled && setLatest(v))
      .catch((e) => console.error("Error reading the latest app version:", e));
    getSubjectNames()
      .then((names) => {
        if (cancelled) return;
        setSubjectNames(names);
        // The same query shape as the subject view, orderBy included.
        names.forEach((name) =>
          countStudents(studentQuerySpec({ subject: name }))
            .then((n) => !cancelled && setSubjectCounts((prev) => ({ ...prev, [name]: n })))
            .catch((e) => console.error("Error counting students:", e))
        );
      })
      .catch((e) => console.error("Error loading subjects:", e));
    return () => {
      cancelled = true;
    };
  }, [countFilters]);

  useEffect(() => {
    if (!latest) return undefined;
    let cancelled = false;
    countFilters(
      STUDENT_FILTERS.filter((f) => f.needsLatest),
      latest
    ).then((c) => !cancelled && setCounts((prev) => ({ ...prev, ...c })));
    return () => {
      cancelled = true;
    };
  }, [latest, countFilters]);

  // Only an app filter reads `latest`; for any other view its arrival must not reload the page.
  const queryLatest = STUDENT_FILTERS.find((f) => f.key === filter)?.needsLatest ? latest : null;

  const loadPage = useCallback(
    async (nextPage) => {
      const token = ++loadToken.current;
      setLoading(true);
      try {
        const spec = studentQuerySpec({ filter, sort, subject, latest: queryLatest });
        const cursor = nextPage === 0 ? null : cursors.current[nextPage - 1] || null;
        const [data, count] = await Promise.all([
          getStudentsPage(spec, { pageSize, cursor }),
          // The count is the pagination total, not the page: if it fails the
          // rows still show and the footer drops its "of N".
          nextPage === 0
            ? countStudents(spec).catch((e) => {
                console.error("Error counting students:", e);
                return null;
              })
            : Promise.resolve(null),
        ]);
        // A newer load started while this one was in flight (filter changed,
        // page size changed): its result wins, this one must not touch state.
        if (token !== loadToken.current) return;
        cursors.current[nextPage] = data.cursor;
        setRows(data.rows);
        setHasMore(data.hasMore);
        if (nextPage === 0) setTotal(count);
        setPage(nextPage);
        setError("");
        await loadChildren(data.rows);
      } catch (e) {
        if (token !== loadToken.current) return;
        console.error("Error loading students:", e);
        // Error State 10618:2251: always say whether anything was changed.
        setError(isMissingIndexError(e) ? `${MISSING_INDEX} Nothing has been changed.` : "Nothing has been changed.");
      } finally {
        if (token === loadToken.current) setLoading(false);
      }
    },
    [filter, sort, subject, queryLatest, pageSize, loadChildren]
  );

  // A new filter, sort or page size starts again from page 1 with fresh cursors and a fresh
  // count, so the head says "Loading students…" rather than the last filter's number, and
  // Previous and Next stay off until the new first page lands (P40).
  useEffect(() => {
    cursors.current = [];
    setTotal(null);
    setPage(0);
    setHasMore(false);
    loadPage(0);
  }, [loadPage]);

  // Search replaces the page with what searchPeople finds (exact phone, prefix
  // name — spec 024 D4), debounced, and dropped if a newer term is in flight.
  useEffect(() => {
    if (!isSearchTerm(searchTerm)) {
      setSearchRows(null);
      setSearching(false);
      return undefined;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const students = await searchPeople(searchTerm, { role: "student" });
        if (stale) return;
        setSearchRows(students);
        loadChildren(students);
      } catch (e) {
        console.error("Error searching students:", e);
        if (!stale) setSearchRows([]);
      } finally {
        if (!stale) setSearching(false);
      }
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [searchTerm, loadChildren]);

  // Ctrl+K deep link (spec 024 D4/T9) and every older link: ?open=<id> now lands on
  // the student page (044 D29). `replace` so back does not return to the redirect.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) navigate(`/students/${openId}`, { replace: true });
  }, [searchParams, navigate]);

  const refreshAfterWrite = () => loadPage(page);

  const openEdit = (row) => {
    setEditTarget(row);
    setEditOpen(true);
  };

  // 059: contactSummary is written by the onStaffNoteWritten trigger a moment after the note, so
  // re-read that one student at ~1.5 s, and again at ~4 s if the summary has not changed yet.
  // A failed read is silent — the note saved. Timers die with the page.
  useEffect(() => () => noteTimers.current.forEach(clearTimeout), []);
  const refreshContact = (row) => {
    const before = JSON.stringify(row.contactSummary || null);
    const reread = (last) =>
      noteTimers.current.push(
        setTimeout(async () => {
          const fresh = await getUserById(row.id);
          if (!fresh) return;
          const swap = (list) => list && list.map((r) => (r.id === row.id ? fresh : r));
          setRows(swap);
          setSearchRows(swap);
          if (!last && JSON.stringify(fresh.contactSummary || null) === before) reread(true);
        }, last ? 2500 : 1500)
      );
    reread(false);
  };

  const visibleRows = searchRows || rows;
  const failed = Boolean(error) && !searchRows; // a search's rows still show after a failed page
  const empty = emptyState({ filter, subject, term: searchRows ? searchTerm : "" });
  const filterViews = STUDENT_FILTERS.filter((f) => !f.needsLatest || latest);
  const chooseFilter = (key) => {
    setFilter(key);
    setSubject("");
  };
  const chooseSubject = (name) => {
    setSubject(name);
    if (name) {
      setFilter("");
      setSort("newest");
    }
  };
  const chooseSort = (key) => {
    setSort(key);
    setSubject("");
  };
  const subjectOptions = [
    { key: "", label: "Any subject" },
    ...subjectNames.map((name) => ({
      key: name,
      label: typeof subjectCounts[name] === "number" ? `${name} · ${countText(subjectCounts[name])}` : name,
    })),
  ];
  const lastPage = total !== null && page >= Math.ceil(total / pageSize) - 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* The head row is Transactions' (044 D50): the title, and the head buttons at its right. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead
          caption="People"
          title="Students"
          subtitle={listSubtitle({
            filter,
            subject,
            total,
            all: counts[""] ?? null,
            loading,
            error: failed,
            searching,
            found: searchRows,
            term: searchTerm,
          })}
        />
        {/* 059 D11: the sidebar lists no sub-pages, so the report opens from here. */}
        <Button onClick={() => navigate("/students/contact-report")} disableRipple disableFocusRipple sx={headButtonSx}>
          <RiContactsBookLine size={18} />
          Contact report
        </Button>
      </Box>

      <ViewChips
        caption="Filters"
        views={filterViews}
        value={filter}
        counts={counts}
        onChange={chooseFilter}
        disabled={Boolean(searchRows) || searching}
      />

      <TableCard
        toolbar={
          <>
            <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Search by phone or name…" />
            <ToolbarMenu
              id="students-sort"
              label="Sort"
              value={filter ? "newest" : sort}
              options={STUDENT_SORTS}
              onChange={chooseSort}
              disabled={Boolean(filter) || Boolean(searchRows) || searching}
              hint={filter ? "A filter shows newest first" : searchRows || searching ? "Clear the search to sort" : undefined}
            />
            {subjectNames.length > 0 && (
              <ToolbarMenu
                id="students-subject"
                label="Subject"
                value={subject}
                options={subjectOptions}
                onChange={chooseSubject}
                disabled={Boolean(searchRows) || searching}
                hint={searchRows || searching ? "Clear the search to filter by subject" : undefined}
              />
            )}
          </>
        }
        footer={
          !searchRows &&
          !searching && (
            <Pager
              label={loading || error ? "" : showingOfLabel(page, rows.length, pageSize, total)}
              page={page}
              hasMore={hasMore && !lastPage}
              onPage={loadPage}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              onPageSize={setPageSize}
            />
          )
        }
      >
        <TableContainer>
          <Table aria-busy={loading || searching}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || searching ? (
                <SkeletonRows columns={COLUMNS.length} />
              ) : failed || visibleRows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS.length}
                  error={failed ? "Could not load students" : ""}
                  errorMessage={error}
                  onRetry={() => loadPage(page)}
                  emptyText={empty.title}
                  emptyMessage={empty.message}
                  emptyActions={
                    empty.clear && (
                      <StateAction outlined onClick={() => (empty.clear === "search" ? setSearchTerm("") : empty.clear === "subject" ? setSubject("") : setFilter(""))}>
                        {empty.clear === "search" ? "Clear search" : empty.clear === "subject" ? "Clear subject" : "Clear filter"}
                      </StateAction>
                    )
                  }
                />
              ) : (
                visibleRows.map((s) => {
                  const joined = dateTimeLines(s.createdAt);
                  const chips = statusChips(s);
                  const contact = contactCell(s.contactSummary);
                  return (
                    <TableRow key={s.id} hover onClick={() => navigate(`/students/${s.id}`)} sx={{ cursor: "pointer" }}>
                      <TableCell>
                        <PersonCell name={s.name || "Unknown"} colorKey={s.id} sub={phoneOf(s) || s.email || "No contact"} />
                      </TableCell>
                      <TableCell>{learnersLabel(s, childNames[s.id])}</TableCell>
                      <TableCell>
                        <SubjectChips interests={interestsOf(s)} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{appCell(s.client)}</TableCell>
                      <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{sessionsLabel(s.WalletBalance)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{lastLessonLabel(s.lessonSummary)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{nextLessonLabel(s.lessonSummary)}</TableCell>
                      <TableCell>
                        {chips.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {chips.map((c) => (
                              <Chip key={c.label} label={c.label} color={c.color} />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {/* 059 D13–D17: the last contact's outcome, its date, and how many contacts so far. */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {contact ? (
                          <Box>
                            <Chip label={contact.chip.label} color={contact.chip.color} />
                            {contact.sub && (
                              <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary, mt: "4px" }}>
                                {contact.sub}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            Not contacted
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {joined ? (
                          <TwoLineCell top={joined.date} bottom={joined.time} />
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {/* The row opens the student; its buttons must not open it a second time. */}
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        <EyeButton to={`/students/${s.id}`} label={`Open ${s.name || "student"}`} />
                        <RowAction Icon={RiStickyNoteLine} label={`Add note for ${s.name || "student"}`} onClick={() => setNoteTarget(s)} />
                        {canEdit && <RowAction Icon={RiPencilLine} label={`Edit ${s.name || "student"}`} onClick={() => openEdit(s)} />}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <EditStudentDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        student={editTarget}
        actingUser={user}
        onSaved={refreshAfterWrite}
      />
      <AddNoteDialog
        open={Boolean(noteTarget)}
        onClose={() => setNoteTarget(null)}
        personUid={noteTarget?.id}
        actingUser={user}
        contactAllowed
        initialType="contact"
        onSaved={(message) => {
          setSnack({ message });
          refreshContact(noteTarget);
        }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity="success" onClose={() => setSnack(null)}>{snack?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Students;
